import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthenticatedUser, AuthService } from 'src/app/core/auth/auth.service-api';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { LoadingIndicatorService } from 'src/app/data/services/loading-indicator.service';
import { UserPermissionService } from 'src/app/data/services/user-permission.service';
import { TieredMenuModule } from 'primeng/tieredmenu';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [TieredMenuModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit, OnDestroy {
  //#region 'Variables'
  public PERMISSIONS: any;
  public userName = '';
  public refineryName = '';
  public isDarkTheme = false;
  menuItems: MenuItem[];
  adminMenuItems: MenuItem[];
  reportingMenuItems: MenuItem[];
  public isMCBU = false;
  private superAdminSubscription: Subscription;
  private verifiedUserSubscription: Subscription;
  //#endregion

  //#region 'Angular Life Cycle'
  constructor(
    private router: Router,
    private _loader: LoadingIndicatorService,
    private cdRef: ChangeDetectorRef,
    public userPermissionService: UserPermissionService,
    private authService: AuthService,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this._loader.show();
    this.verifiedUserSubscription = this.authService.verifiedUser$.subscribe(user => {
      if (user) {
        this.applyUser(user);
      }
    });
    this.getPermissionsData();
    this.executeAdminMenuItems();
    this.executeReportingMenuItems();
    this.executeHelpMenuItems();

    // initialize theme from localStorage
    try {
      const stored = localStorage.getItem('darkTheme');
      this.isDarkTheme = stored === 'true';
    } catch (e) {
      this.isDarkTheme = false;
    }

    this.setBodyTheme();
  }

  ngOnDestroy(): void {
    this.superAdminSubscription?.unsubscribe();
    this.verifiedUserSubscription?.unsubscribe();
  }
  //#endregion

  //#region 'General Methods'
  private getPermissionsData(): void {
    this.PERMISSIONS = JSON.parse(localStorage.getItem('user'));
    if (this.PERMISSIONS) {
      const verifiedUser = this.authService.verifiedUser;
      if (
        verifiedUser?.userId === this.PERMISSIONS.userId &&
        Number(verifiedUser.refineryId) === Number(this.PERMISSIONS.refineryId) &&
        Number(verifiedUser.roleId) === Number(this.PERMISSIONS.roleId)
      ) {
        this.applyUser(verifiedUser);
        return;
      }
      // Show the access saved at login right away; the background recheck and route guard keep it accurate.
      this.PERMISSIONS.isAdminAtAllSites = this.PERMISSIONS.isAdminAtAllSites === true;
      this.applyUser(this.PERMISSIONS);
      this.superAdminSubscription?.unsubscribe();
      this.superAdminSubscription = this.authService.refreshSuperAdminAccess().subscribe(isSuperAdmin => {
        this.applyUser({ ...this.PERMISSIONS, isAdminAtAllSites: isSuperAdmin });
      });
    }
  }

  private applyUser(user: AuthenticatedUser): void {
    this.ngZone.run(() => {
      this.PERMISSIONS = user;
      this.userName = user.userName;
      this.refineryName = user.refineryName;
      this.executeAdminMenuItems();
      this.executeHelpMenuItems();
      this._loader.hide();
      this.cdRef.markForCheck();
    });
  }
  /* tslint:disable-next-line */
  public validatePermissions(type: string): boolean {
    if (!this.PERMISSIONS) {
      this.getPermissionsData();
    }
    return this.userPermissionService.hasPermission(type, this.PERMISSIONS);
  }

  routePage(path: string) {
    this.router.navigateByUrl(path);
  }

  public showEnvironment(): string {
    if (window.location.origin.includes('dev')) {
      return '[Dev]';
    } else if (window.location.origin.includes('test')) {
      return '[Test]';
    } else if (window.location.origin.includes('localhost')) {
      return '[LocalHost]';
    } else {
      return '';
    }
  }

  public isDevelopmentEnvironment(): boolean {
    return window.location.hostname === 'localhost';
  }

  public executeHelpMenuItems() {
    this.isMCBU = this.refineryName === 'Mid-Continent Business Unit';

    this.menuItems = [
      {
        label: 'Training Guide',
        url: '../../../../assets/docs/Weld Manager Training_R3.pdf',
        target: '_blank'
      },
      {
        label: 'Bulk Upload Template',
        url: this.isMCBU
          ? '../../../../assets/docs/BulkUploadTemplate_mcbu_v3.xlsx'
          : '../../../../assets/docs/BulkUploadTemplate_non_mcbu_v3.xlsx',
        target: '_top'
      },
      {
        label: 'Submit Request',
        url: 'https://chevron.service-now.com/itserviceportal?id=sc_cat_item_guide&sys_id=9c08a21edb126450182e1dca48961981&sysparm_u_appid=5054022',
        target: '_blank'
      },
      {
        label: 'Switch Site',
        routerLink: ['/admin/switchsite']
      }
    ];
  }

  public executeAdminMenuItems() {
    this.adminMenuItems = [
      {
        label: 'Email Settings', //email data is managed in the Refinery table
        routerLink: ['/admin/email']
      },
      {
        label: 'Divisions',
        routerLink: ['/admin/division']
      },
      {
        label: 'Filler Materials',
        routerLink: ['/admin/filler-materials']
      },
      {
        label: 'Groups',
        routerLink: ['/admin/group']
      },
      {
        label: 'Materials',
        routerLink: ['/admin/materials']
      },
      {
        label: 'Pipe Sizes',
        routerLink: ['/admin/pipe-sizes']
      },
      {
        label: 'Role Permissions',
        routerLink: ['/admin/rolepermission']
      },
      {
        label: 'RT Percentage',
        routerLink: ['/admin/rtpercentage']
      },
      {
        label: 'Users',
        routerLink: ['/admin/user']
      },
      {
        label: 'Units',
        routerLink: ['/admin/unit']
      },
      {
        label: 'Welder Info',
        items: [
          {
            label: 'Welders',
            routerLink: ['/admin/welder']
          },
          {
            label: 'Contract Company',
            routerLink: ['/admin/contract-company']
          },
          {
            label: 'Contract Welder Photo',
            routerLink: ['/admin/contract-welder']
          }
        ]
      },
      {
        label: 'Weld Process',
        routerLink: ['/admin/weldprocess']
      },
      {
        label: 'WPS',
        routerLink: ['/admin/wps']
      }
    ];

    if (this.userPermissionService.isSuperAdmin(this.PERMISSIONS)) {
      this.adminMenuItems.push({
        label: 'Super Admin',
        routerLink: ['/admin/super-admin']
      });
    }
  }

  public executeReportingMenuItems() {
    this.reportingMenuItems = [
      {
        label: 'Weld Stats',
        routerLink: ['/reporting/weld-stats']
      },
      {
        label: 'Welder RT Reporting',
        routerLink: ['/reporting/welder-stats']
      },
      {
        label: 'Welder Stamp ID History',
        routerLink: ['/reporting/welder-stamp-id-history']
      }
    ];
  }

  //#region 'Theme'
  private setBodyTheme(): void {
    try {
      if (this.isDarkTheme) {
        document.body.classList.add('dark-theme');
        document.body.classList.remove('light-theme');
      } else {
        document.body.classList.remove('dark-theme');
        document.body.classList.add('light-theme');
      }
    } catch (e) {
      // ignore: server-side rendering or test environments
    }
  }

  public toggleTheme(): void {
    this.isDarkTheme = !this.isDarkTheme;
    try {
      localStorage.setItem('darkTheme', String(this.isDarkTheme));
      localStorage.setItem('themeChosen', 'true');
    } catch (e) {
      // ignore
    }
    this.setBodyTheme();
    // Force overlay panels to refresh their styles (Edge may cache panel styles)
    try {
      const panels = document.querySelectorAll(
        '.p-select-overlay, .p-multiselect-overlay, .p-overlaypanel, .p-overlay, .p-tooltip'
      );
      panels.forEach((el: Element) => {
        const htmlEl = el as HTMLElement;
        // trigger reflow by toggling display
        const prev = htmlEl.style.display;
        htmlEl.style.display = 'none';
        // read offsetHeight to force reflow
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        htmlEl.offsetHeight;
        htmlEl.style.display = prev || '';
      });
    } catch (e) {
      // ignore
    }
    this.cdRef.markForCheck();
  }
  //#endregion
  //#endregion
}
